import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Rocket,
  Server,
  Cloud,
  Wallet,
  Cpu,
  MessageSquare,
  CreditCard,
  PlayCircle,
  Link as LinkIcon,
  Shield,
  HelpCircle,
  ArrowRight,
  Check,
  Zap,
} from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
}

export function Docs() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string>('introduction');

  const SECTIONS: Section[] = useMemo(
    () => [
      { id: 'introduction', title: t('landing.docs.sections.introduction.title'), icon: <BookOpen className="h-4 w-4" /> },
      { id: 'what-is-openclaw', title: t('landing.docs.sections.whatIsOpenclaw.title'), icon: <Cpu className="h-4 w-4" /> },
      { id: 'what-is-setopenclaw', title: t('landing.docs.sections.whatIsSetopenclaw.title'), icon: <Rocket className="h-4 w-4" /> },
      { id: 'getting-started', title: t('landing.docs.sections.gettingStarted.title'), icon: <Zap className="h-4 w-4" /> },
      { id: 'hosting-modes', title: t('landing.docs.sections.hostingModes.title'), icon: <Server className="h-4 w-4" /> },
      { id: 'configure', title: t('landing.docs.sections.configure.title'), icon: <Cpu className="h-4 w-4" /> },
      { id: 'review-pay', title: t('landing.docs.sections.reviewPay.title'), icon: <CreditCard className="h-4 w-4" /> },
      { id: 'deploy', title: t('landing.docs.sections.deploy.title'), icon: <PlayCircle className="h-4 w-4" /> },
      { id: 'connect', title: t('landing.docs.sections.connect.title'), icon: <LinkIcon className="h-4 w-4" /> },
      { id: 'channels', title: t('landing.docs.sections.channels.title'), icon: <MessageSquare className="h-4 w-4" /> },
      { id: 'security', title: t('landing.docs.sections.security.title'), icon: <Shield className="h-4 w-4" /> },
      { id: 'faq', title: t('landing.docs.sections.faq.title'), icon: <HelpCircle className="h-4 w-4" /> },
    ],
    [t],
  );

  const AI_MODELS = useMemo(
    () => [
      { name: t('landing.docs.models.gpt4oMini.name'), provider: t('landing.docs.models.gpt4oMini.provider'), tagline: t('landing.docs.models.gpt4oMini.tagline'), estCost: t('landing.docs.models.gpt4oMini.cost') },
      { name: t('landing.docs.models.gpt4o.name'), provider: t('landing.docs.models.gpt4o.provider'), tagline: t('landing.docs.models.gpt4o.tagline'), estCost: t('landing.docs.models.gpt4o.cost') },
      { name: t('landing.docs.models.claudeSonnet4.name'), provider: t('landing.docs.models.claudeSonnet4.provider'), tagline: t('landing.docs.models.claudeSonnet4.tagline'), estCost: t('landing.docs.models.claudeSonnet4.cost') },
      { name: t('landing.docs.models.gemini2Flash.name'), provider: t('landing.docs.models.gemini2Flash.provider'), tagline: t('landing.docs.models.gemini2Flash.tagline'), estCost: t('landing.docs.models.gemini2Flash.cost') },
      { name: t('landing.docs.models.llama3370b.name'), provider: t('landing.docs.models.llama3370b.provider'), tagline: t('landing.docs.models.llama3370b.tagline'), estCost: t('landing.docs.models.llama3370b.cost') },
    ],
    [t],
  );

  const CHANNELS = useMemo(
    () => [
      { id: 'whatsapp', name: t('landing.docs.channels.whatsapp.name'), description: t('landing.docs.channels.whatsapp.description') },
      { id: 'telegram', name: t('landing.docs.channels.telegram.name'), description: t('landing.docs.channels.telegram.description') },
      { id: 'discord', name: t('landing.docs.channels.discord.name'), description: t('landing.docs.channels.discord.description') },
      { id: 'line', name: t('landing.docs.channels.line.name'), description: t('landing.docs.channels.line.description') },
      { id: 'slack', name: t('landing.docs.channels.slack.name'), description: t('landing.docs.channels.slack.description') },
    ],
    [t],
  );

  const HOSTING_MODES = useMemo(
    () => [
      {
        key: 'byos',
        name: t('landing.docs.hostingModes.byos.name'),
        price: '$5',
        priceSuffix: t('landing.docs.hostingModes.byos.priceSuffix'),
        icon: <Server className="h-6 w-6" />,
        color: 'from-orange-500 to-amber-500',
        badge: undefined as string | undefined,
        bullets: t('landing.docs.hostingModes.byos.bullets', { returnObjects: true }) as string[],
      },
      {
        key: 'managed',
        name: t('landing.docs.hostingModes.managed.name'),
        price: '$10',
        priceSuffix: t('landing.docs.hostingModes.managed.priceSuffix'),
        icon: <Cloud className="h-6 w-6" />,
        color: 'from-emerald-500 to-teal-500',
        badge: undefined as string | undefined,
        bullets: t('landing.docs.hostingModes.managed.bullets', { returnObjects: true }) as string[],
      },
      {
        key: 'managed-wallet',
        name: t('landing.docs.hostingModes.managedWallet.name'),
        price: '$10',
        priceSuffix: t('landing.docs.hostingModes.managedWallet.priceSuffix'),
        icon: <Wallet className="h-6 w-6" />,
        color: 'from-teal-500 to-cyan-500',
        badge: t('landing.docs.hostingModes.managedWallet.badge') as string | undefined,
        bullets: t('landing.docs.hostingModes.managedWallet.bullets', { returnObjects: true }) as string[],
      },
    ],
    [t],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px' },
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [SECTIONS]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA4IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40" />

        {/* Floating OpenClaw logo - decorative */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotate: -15 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="hidden md:block absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-white/30 blur-3xl rounded-full" />
            <img
              src="/openclaw.svg"
              alt=""
              className="relative h-56 w-56 lg:h-72 lg:w-72 drop-shadow-2xl"
            />
          </div>
        </motion.div>

        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-sm px-4 py-1.5 text-sm text-white mb-6 border border-white/30">
              <BookOpen className="h-4 w-4" />
              {t('landing.docs.hero.badge')}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('landing.docs.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              {t('landing.docs.hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <div className="lg:sticky lg:top-24">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                {t('landing.docs.sidebar.onThisPage')}
              </h3>
              <nav className="space-y-1">
                {SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollTo(section.id)}
                    className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-left transition-colors ${
                      activeId === section.id
                        ? 'bg-emerald-50 text-emerald-700 font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {section.icon}
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 max-w-3xl">
            {/* Introduction */}
            <Section id="introduction" title={t('landing.docs.sections.introduction.title')}>
              {/* Welcome splash with OpenClaw logo */}
              <div className="not-prose relative overflow-hidden rounded-2xl border bg-gradient-to-br from-emerald-50 via-white to-red-50/40 p-6 my-6">
                <div className="flex items-center gap-5">
                  <div className="shrink-0">
                    <img
                      src="/openclaw-logo.avif"
                      alt="OpenClaw"
                      className="h-20 w-20 object-contain drop-shadow-md"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-1">
                      {t('landing.docs.sections.introduction.welcomeBadge')}
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-1">
                      {t('landing.docs.sections.introduction.welcomeTitle')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('landing.docs.sections.introduction.welcomeSubtitle')}
                    </p>
                  </div>
                </div>
              </div>

              <p>{t('landing.docs.sections.introduction.p1')}</p>
              <p>{t('landing.docs.sections.introduction.p2')}</p>
              <Callout>
                <strong>{t('landing.docs.sections.introduction.calloutPrefix')}</strong>{' '}
                {t('landing.docs.sections.introduction.calloutText')}{' '}
                <a href="#getting-started" onClick={(e) => { e.preventDefault(); scrollTo('getting-started'); }} className="text-emerald-600 font-medium underline underline-offset-2">
                  {t('landing.docs.sections.introduction.calloutLink')}
                </a>{' '}
                {t('landing.docs.sections.introduction.calloutSuffix')}
              </Callout>
            </Section>

            {/* What is OpenClaw */}
            <Section id="what-is-openclaw" title={t('landing.docs.sections.whatIsOpenclaw.title')}>
              {/* Branded hero card */}
              <div className="not-prose relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 border border-red-100 p-8 my-6">
                <div className="absolute -right-12 -bottom-12 opacity-20 pointer-events-none">
                  <img src="/openclaw.svg" alt="" className="h-64 w-64" />
                </div>
                <div className="relative flex items-start gap-6">
                  <div className="shrink-0 flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-lg border border-red-100">
                    <img src="/openclaw.svg" alt={t('landing.docs.sections.whatIsOpenclaw.logoAlt')} className="h-14 w-14" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-1">
                      {t('landing.docs.sections.whatIsOpenclaw.openSourceBadge')}
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">{t('landing.docs.sections.whatIsOpenclaw.cardTitle')}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t('landing.docs.sections.whatIsOpenclaw.cardDescription')}
                    </p>
                  </div>
                </div>
              </div>

              <p>
                <strong>{t('landing.docs.sections.whatIsOpenclaw.p1Strong')}</strong>{' '}
                {t('landing.docs.sections.whatIsOpenclaw.p1Rest')}
              </p>
              <p>
                {t('landing.docs.sections.whatIsOpenclaw.p2Start')}{' '}
                <strong>{t('landing.docs.sections.whatIsOpenclaw.p2Strong')}</strong>{' '}
                {t('landing.docs.sections.whatIsOpenclaw.p2End')}
              </p>

              {/* Feature showcase with avif logo */}
              <div className="not-prose grid sm:grid-cols-3 gap-3 my-6">
                <div className="rounded-xl bg-card border p-4 text-center">
                  <div className="flex justify-center mb-2">
                    <img src="/openclaw.svg" alt="" className="h-10 w-10" />
                  </div>
                  <div className="font-semibold text-sm text-foreground">{t('landing.docs.sections.whatIsOpenclaw.feature1Title')}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('landing.docs.sections.whatIsOpenclaw.feature1Description')}
                  </p>
                </div>
                <div className="rounded-xl bg-card border p-4 text-center">
                  <div className="flex justify-center mb-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                      <Cpu className="h-5 w-5 text-emerald-600" />
                    </div>
                  </div>
                  <div className="font-semibold text-sm text-foreground">{t('landing.docs.sections.whatIsOpenclaw.feature2Title')}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('landing.docs.sections.whatIsOpenclaw.feature2Description')}
                  </p>
                </div>
                <div className="rounded-xl bg-card border p-4 text-center">
                  <div className="flex justify-center mb-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                      <MessageSquare className="h-5 w-5 text-emerald-600" />
                    </div>
                  </div>
                  <div className="font-semibold text-sm text-foreground">{t('landing.docs.sections.whatIsOpenclaw.feature3Title')}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('landing.docs.sections.whatIsOpenclaw.feature3Description')}
                  </p>
                </div>
              </div>
            </Section>

            {/* What is SetOpenClaw */}
            <Section id="what-is-setopenclaw" title={t('landing.docs.sections.whatIsSetopenclaw.title')}>
              <p>
                <strong>{t('landing.docs.sections.whatIsSetopenclaw.p1Strong')}</strong>{' '}
                {t('landing.docs.sections.whatIsSetopenclaw.p1Rest')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4 not-prose my-6">
                <FeatureCard
                  icon={<Zap className="h-5 w-5 text-emerald-600" />}
                  title={t('landing.docs.sections.whatIsSetopenclaw.card1Title')}
                  description={t('landing.docs.sections.whatIsSetopenclaw.card1Description')}
                />
                <FeatureCard
                  icon={<Shield className="h-5 w-5 text-emerald-600" />}
                  title={t('landing.docs.sections.whatIsSetopenclaw.card2Title')}
                  description={t('landing.docs.sections.whatIsSetopenclaw.card2Description')}
                />
                <FeatureCard
                  icon={<MessageSquare className="h-5 w-5 text-emerald-600" />}
                  title={t('landing.docs.sections.whatIsSetopenclaw.card3Title')}
                  description={t('landing.docs.sections.whatIsSetopenclaw.card3Description')}
                />
                <FeatureCard
                  icon={<Cpu className="h-5 w-5 text-emerald-600" />}
                  title={t('landing.docs.sections.whatIsSetopenclaw.card4Title')}
                  description={t('landing.docs.sections.whatIsSetopenclaw.card4Description')}
                />
              </div>
            </Section>

            {/* Getting Started */}
            <Section id="getting-started" title={t('landing.docs.sections.gettingStarted.title')}>
              <p>{t('landing.docs.sections.gettingStarted.intro')}</p>
              <ol className="space-y-3 my-5">
                <StepItem n={1} title={t('landing.docs.sections.gettingStarted.step1Title')} description={t('landing.docs.sections.gettingStarted.step1Description')} />
                <StepItem n={2} title={t('landing.docs.sections.gettingStarted.step2Title')} description={t('landing.docs.sections.gettingStarted.step2Description')} />
                <StepItem n={3} title={t('landing.docs.sections.gettingStarted.step3Title')} description={t('landing.docs.sections.gettingStarted.step3Description')} />
                <StepItem n={4} title={t('landing.docs.sections.gettingStarted.step4Title')} description={t('landing.docs.sections.gettingStarted.step4Description')} />
                <StepItem n={5} title={t('landing.docs.sections.gettingStarted.step5Title')} description={t('landing.docs.sections.gettingStarted.step5Description')} />
                <StepItem n={6} title={t('landing.docs.sections.gettingStarted.step6Title')} description={t('landing.docs.sections.gettingStarted.step6Description')} />
                <StepItem n={7} title={t('landing.docs.sections.gettingStarted.step7Title')} description={t('landing.docs.sections.gettingStarted.step7Description')} />
              </ol>
              <DocImage src="/step-1.png" alt={t('landing.docs.sections.gettingStarted.imageAlt')} caption={t('landing.docs.sections.gettingStarted.imageCaption')} />
            </Section>

            {/* Hosting Modes */}
            <Section id="hosting-modes" title={t('landing.docs.sections.hostingModes.title')}>
              <p>{t('landing.docs.sections.hostingModes.intro')}</p>
              <DocImage src="/step-2.png" alt={t('landing.docs.sections.hostingModes.imageAlt')} caption={t('landing.docs.sections.hostingModes.imageCaption')} />
              <div className="space-y-5 my-6 not-prose">
                {HOSTING_MODES.map((mode) => (
                  <div key={mode.key} className="rounded-xl border bg-card p-6 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${mode.color} text-white shadow-md`}>
                        {mode.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold">{mode.name}</h3>
                          {mode.badge && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                              {mode.badge}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 mb-3">
                          <span className="text-2xl font-bold text-emerald-600">{mode.price}</span>
                          <span className="text-sm text-muted-foreground ml-1">{mode.priceSuffix}</span>
                        </div>
                        <ul className="space-y-1.5">
                          {mode.bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Callout variant="tip">
                <strong>{t('landing.docs.sections.hostingModes.calloutStrong')}</strong>{' '}
                {t('landing.docs.sections.hostingModes.calloutMiddle1')}{' '}
                <strong>{t('landing.docs.sections.hostingModes.calloutStrong2')}</strong>{' '}
                {t('landing.docs.sections.hostingModes.calloutEnd')}
              </Callout>
            </Section>

            {/* Configure */}
            <Section id="configure" title={t('landing.docs.sections.configure.title')}>
              <p>{t('landing.docs.sections.configure.intro')}</p>
              <DocImage src="/step-3.png" alt={t('landing.docs.sections.configure.imageAlt')} caption={t('landing.docs.sections.configure.imageCaption')} />

              <h3 className="text-lg font-semibold mt-8 mb-3">{t('landing.docs.sections.configure.instanceNameHeading')}</h3>
              <p>
                {t('landing.docs.sections.configure.instanceNameP1')}{' '}
                <em>{t('landing.docs.sections.configure.instanceNameExample1')}</em>,{' '}
                <em>{t('landing.docs.sections.configure.instanceNameExample2')}</em>,
                <em>{t('landing.docs.sections.configure.instanceNameExample3')}</em>.{' '}
                {t('landing.docs.sections.configure.instanceNameP2')}
              </p>

              <h3 className="text-lg font-semibold mt-8 mb-3">{t('landing.docs.sections.configure.aiModelHeading')}</h3>
              <p>{t('landing.docs.sections.configure.aiModelIntro')}</p>
              <div className="grid sm:grid-cols-2 gap-3 my-5 not-prose">
                {AI_MODELS.map((model) => (
                  <div key={model.name} className="rounded-lg border p-4 bg-card">
                    <div className="font-semibold text-sm">{model.name}</div>
                    <div className="text-xs text-muted-foreground">{model.provider}</div>
                    <p className="text-sm text-muted-foreground mt-2">{model.tagline}</p>
                    <div className="text-xs font-medium text-emerald-600 mt-2">{model.estCost}</div>
                  </div>
                ))}
              </div>
              <Callout>
                {t('landing.docs.sections.configure.aiModelCallout')}
              </Callout>

              <h3 className="text-lg font-semibold mt-8 mb-3">{t('landing.docs.sections.configure.channelsHeading')}</h3>
              <p>
                {t('landing.docs.sections.configure.channelsP1')}{' '}
                <a href="#channels" onClick={(e) => { e.preventDefault(); scrollTo('channels'); }} className="text-emerald-600 font-medium underline underline-offset-2">
                  {t('landing.docs.sections.configure.channelsLink')}
                </a>{' '}
                {t('landing.docs.sections.configure.channelsP2')}
              </p>
            </Section>

            {/* Review & Pay */}
            <Section id="review-pay" title={t('landing.docs.sections.reviewPay.title')}>
              <p>
                {t('landing.docs.sections.reviewPay.intro1')}{' '}
                <strong>{t('landing.docs.sections.reviewPay.introStrong')}</strong>.
              </p>
              <DocImage src="/step-4.png" alt={t('landing.docs.sections.reviewPay.imageAlt')} caption={t('landing.docs.sections.reviewPay.imageCaption')} />
              <p>{t('landing.docs.sections.reviewPay.summaryIntro')}</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground my-3">
                <li><strong>{t('landing.docs.sections.reviewPay.bullet1Strong')}</strong> {t('landing.docs.sections.reviewPay.bullet1Rest')}</li>
                <li><strong>{t('landing.docs.sections.reviewPay.bullet2Strong')}</strong> {t('landing.docs.sections.reviewPay.bullet2Rest')}</li>
                <li><strong>{t('landing.docs.sections.reviewPay.bullet3Strong')}</strong> {t('landing.docs.sections.reviewPay.bullet3Rest')}</li>
                <li><strong>{t('landing.docs.sections.reviewPay.bullet4Strong')}</strong> {t('landing.docs.sections.reviewPay.bullet4Rest')}</li>
                <li><strong>{t('landing.docs.sections.reviewPay.bullet5Strong')}</strong> {t('landing.docs.sections.reviewPay.bullet5Rest')}</li>
                <li><strong>{t('landing.docs.sections.reviewPay.bullet6Strong')}</strong> {t('landing.docs.sections.reviewPay.bullet6Rest')}</li>
              </ul>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('landing.docs.sections.reviewPay.paymentMethodsHeading')}</h3>
              <div className="grid sm:grid-cols-2 gap-4 not-prose my-5">
                <div className="rounded-lg border p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="h-5 w-5 text-emerald-600" />
                    <div className="font-semibold">{t('landing.docs.sections.reviewPay.walletTitle')}</div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('landing.docs.sections.reviewPay.walletDescription')}
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-5 w-5 text-emerald-600" />
                    <div className="font-semibold">{t('landing.docs.sections.reviewPay.cardTitle')}</div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('landing.docs.sections.reviewPay.cardDescription')}
                  </p>
                </div>
              </div>
            </Section>

            {/* Deploy */}
            <Section id="deploy" title={t('landing.docs.sections.deploy.title')}>
              <p>
                {t('landing.docs.sections.deploy.p1Start')}{' '}
                <strong>{t('landing.docs.sections.deploy.p1Strong1')}</strong>{' '}
                {t('landing.docs.sections.deploy.p1Middle')}{' '}
                <strong>{t('landing.docs.sections.deploy.p1Strong2')}</strong>{' '}
                {t('landing.docs.sections.deploy.p1End')}
              </p>
              <DocImage src="/step-5.png" alt={t('landing.docs.sections.deploy.imageAlt')} caption={t('landing.docs.sections.deploy.imageCaption')} />
              <p>{t('landing.docs.sections.deploy.handleIntro')}</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground my-3">
                <li>{t('landing.docs.sections.deploy.handle1')}</li>
                <li>{t('landing.docs.sections.deploy.handle2')}</li>
                <li>{t('landing.docs.sections.deploy.handle3')}</li>
                <li>{t('landing.docs.sections.deploy.handle4')}</li>
              </ul>
              <p>
                {t('landing.docs.sections.deploy.statusStart')}{' '}
                <em>{t('landing.docs.sections.deploy.statusFrom')}</em>{' '}
                {t('landing.docs.sections.deploy.statusTo')}{' '}
                <strong className="text-emerald-600">{t('landing.docs.sections.deploy.statusRunning')}</strong>,{' '}
                {t('landing.docs.sections.deploy.statusMiddle')}{' '}
                <strong>{t('landing.docs.sections.deploy.statusServerIp')}</strong>,{' '}
                <strong>{t('landing.docs.sections.deploy.statusDashboardUrl')}</strong>,{' '}
                {t('landing.docs.sections.deploy.statusAnd')}{' '}
                <strong>{t('landing.docs.sections.deploy.statusGatewayPassword')}</strong>{' '}
                {t('landing.docs.sections.deploy.statusEnd')}
              </p>
              <Callout variant="tip">
                {t('landing.docs.sections.deploy.callout')}
              </Callout>
            </Section>

            {/* Connect */}
            <Section id="connect" title={t('landing.docs.sections.connect.title')}>
              <p>
                {t('landing.docs.sections.connect.introStart')}{' '}
                <strong className="text-emerald-600">{t('landing.docs.sections.connect.introRunning')}</strong>,{' '}
                {t('landing.docs.sections.connect.introMiddle')}{' '}
                <strong>{t('landing.docs.sections.connect.introServerIp')}</strong>,{' '}
                {t('landing.docs.sections.connect.introTheDashboardUrl')}{' '}
                <strong>{t('landing.docs.sections.connect.introDashboardUrl')}</strong>,{' '}
                {t('landing.docs.sections.connect.introAnd')}{' '}
                <strong>{t('landing.docs.sections.connect.introGatewayPassword')}</strong>.
              </p>
              <DocImage src="/step-6.png" alt={t('landing.docs.sections.connect.imageAlt')} caption={t('landing.docs.sections.connect.imageCaption')} />

              <h3 className="text-lg font-semibold mt-8 mb-3">{t('landing.docs.sections.connect.step1Heading')}</h3>
              <p>
                {t('landing.docs.sections.connect.step1P1Start')}{' '}
                <strong>{t('landing.docs.sections.connect.step1P1Strong')}</strong>{' '}
                {t('landing.docs.sections.connect.step1P1End')}
              </p>
              <Callout variant="tip">
                {t('landing.docs.sections.connect.step1CalloutStart')}{' '}
                <strong>{t('landing.docs.sections.connect.step1CalloutStrong')}</strong>{' '}
                {t('landing.docs.sections.connect.step1CalloutExample')}{' '}
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                  https://your-bot.setopenclaw.com
                </code>
                {t('landing.docs.sections.connect.step1CalloutEnd')}
              </Callout>

              <h3 className="text-lg font-semibold mt-8 mb-3">{t('landing.docs.sections.connect.step2Heading')}</h3>
              <p>
                {t('landing.docs.sections.connect.step2P1Start')}{' '}
                <strong>{t('landing.docs.sections.connect.step2P1Strong')}</strong>.{' '}
                {t('landing.docs.sections.connect.step2P1End')}
              </p>
              <p>
                {t('landing.docs.sections.connect.step2P2Start')}{' '}
                <strong>{t('landing.docs.sections.connect.step2P2Strong')}</strong>:
              </p>
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground my-3">
                <li>
                  {t('landing.docs.sections.connect.step2Item1Start')}{' '}
                  <strong>{t('landing.docs.sections.connect.step2Item1Strong')}</strong>{' '}
                  {t('landing.docs.sections.connect.step2Item1End')}
                </li>
                <li>
                  {t('landing.docs.sections.connect.step2Item2Start')}{' '}
                  <strong>{t('landing.docs.sections.connect.step2Item2Strong')}</strong>{' '}
                  {t('landing.docs.sections.connect.step2Item2End')}
                </li>
                <li>{t('landing.docs.sections.connect.step2Item3')}</li>
              </ol>
              <Callout variant="warning">
                <strong>{t('landing.docs.sections.connect.step2WarningStrong')}</strong>{' '}
                {t('landing.docs.sections.connect.step2WarningRest')}
              </Callout>

              <h3 className="text-lg font-semibold mt-8 mb-3">{t('landing.docs.sections.connect.step3Heading')}</h3>
              <p>{t('landing.docs.sections.connect.step3Intro')}</p>
              <ul className="list-disc list-inside space-y-1.5 text-muted-foreground my-3">
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.connect.step3Action1Strong')}</strong>{' '}
                  {t('landing.docs.sections.connect.step3Action1Rest')}
                </li>
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.connect.step3Action2Strong')}</strong>{' '}
                  {t('landing.docs.sections.connect.step3Action2Rest')}
                </li>
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.connect.step3Action3Strong')}</strong>{' '}
                  {t('landing.docs.sections.connect.step3Action3Rest')}
                </li>
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.connect.step3Action4Strong')}</strong>{' '}
                  {t('landing.docs.sections.connect.step3Action4Rest')}
                </li>
              </ul>

              <h3 className="text-lg font-semibold mt-8 mb-3">{t('landing.docs.sections.connect.step4Heading')}</h3>
              <p>{t('landing.docs.sections.connect.step4Body')}</p>
            </Section>

            {/* Channels */}
            <Section id="channels" title={t('landing.docs.sections.channels.title')}>
              <p>{t('landing.docs.sections.channels.intro')}</p>
              <div className="grid sm:grid-cols-2 gap-4 my-5 not-prose">
                {CHANNELS.map((channel) => (
                  <div key={channel.id} className="rounded-lg border p-4 bg-card">
                    <div className="font-semibold text-sm mb-1">{channel.name}</div>
                    <p className="text-sm text-muted-foreground">{channel.description}</p>
                  </div>
                ))}
              </div>
              <p>{t('landing.docs.sections.channels.outro')}</p>
            </Section>

            {/* Security */}
            <Section id="security" title={t('landing.docs.sections.security.title')}>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.security.bullet1Strong')}</strong>{' '}
                  {t('landing.docs.sections.security.bullet1Rest')}
                </li>
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.security.bullet2Strong')}</strong>{' '}
                  {t('landing.docs.sections.security.bullet2Rest')}
                </li>
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.security.bullet3Strong')}</strong>{' '}
                  {t('landing.docs.sections.security.bullet3Rest')}
                </li>
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.security.bullet4Strong')}</strong>{' '}
                  {t('landing.docs.sections.security.bullet4Rest')}
                </li>
                <li>
                  <strong className="text-foreground">{t('landing.docs.sections.security.bullet5Strong')}</strong>{' '}
                  {t('landing.docs.sections.security.bullet5Rest')}
                </li>
              </ul>
            </Section>

            {/* FAQ */}
            <Section id="faq" title={t('landing.docs.sections.faq.title')}>
              <div className="space-y-5">
                <FaqItem
                  q={t('landing.docs.sections.faq.q1')}
                  a={t('landing.docs.sections.faq.a1')}
                />
                <FaqItem
                  q={t('landing.docs.sections.faq.q2')}
                  a={t('landing.docs.sections.faq.a2')}
                />
                <FaqItem
                  q={t('landing.docs.sections.faq.q3')}
                  a={t('landing.docs.sections.faq.a3')}
                />
                <FaqItem
                  q={t('landing.docs.sections.faq.q4')}
                  a={t('landing.docs.sections.faq.a4')}
                />
                <FaqItem
                  q={t('landing.docs.sections.faq.q5')}
                  a={t('landing.docs.sections.faq.a5')}
                />
                <FaqItem
                  q={t('landing.docs.sections.faq.q6')}
                  a={t('landing.docs.sections.faq.a6')}
                />
                <FaqItem
                  q={t('landing.docs.sections.faq.q7')}
                  a={t('landing.docs.sections.faq.a7')}
                />
              </div>
            </Section>

            {/* CTA */}
            <div className="relative mt-16 overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 p-10 text-center">
              {/* Decorative background logo */}
              <div className="absolute -right-16 -top-16 opacity-10 pointer-events-none">
                <img src="/openclaw.svg" alt="" className="h-64 w-64" />
              </div>
              <div className="absolute -left-16 -bottom-16 opacity-10 pointer-events-none">
                <img src="/openclaw.svg" alt="" className="h-48 w-48" />
              </div>

              <div className="relative">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 mb-4">
                  <img src="/openclaw.svg" alt="" className="h-10 w-10" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  {t('landing.docs.cta.title')}
                </h2>
                <p className="text-white/90 mb-6 max-w-lg mx-auto">
                  {t('landing.docs.cta.subtitle')}
                </p>
                <Link to="/register">
                  <Button size="lg" variant="outline" className="bg-white text-emerald-600 border-white hover:bg-gray-100 hover:text-emerald-700 font-semibold px-8 gap-2">
                    {t('landing.docs.cta.button')}
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}

// ---------- Helper components ----------

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 mb-16">
      <h2 className="text-3xl font-bold mb-5 pb-3 border-b">{title}</h2>
      <div className="prose prose-neutral max-w-none text-muted-foreground leading-relaxed space-y-4 [&_strong]:text-foreground [&_em]:text-foreground">
        {children}
      </div>
    </section>
  );
}

function Callout({
  children,
  variant = 'info',
}: {
  children: React.ReactNode;
  variant?: 'info' | 'tip' | 'warning';
}) {
  const styles = {
    info: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    tip: 'bg-blue-50 border-blue-200 text-blue-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
  };
  return (
    <div className={`not-prose rounded-lg border-l-4 p-4 my-5 text-sm ${styles[variant]}`}>
      {children}
    </div>
  );
}

function StepItem({ n, title, description }: { n: number; title: string; description: string }) {
  return (
    <li className="flex items-start gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white font-semibold text-sm">
        {n}
      </div>
      <div>
        <div className="font-semibold text-foreground">{title}</div>
        <div className="text-sm text-muted-foreground">{description}</div>
      </div>
    </li>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <div className="font-semibold text-sm text-foreground">{title}</div>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function DocImage({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="not-prose my-6">
      <div className="overflow-hidden rounded-xl border shadow-sm bg-muted/30">
        <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
      </div>
      <figcaption className="text-center text-xs text-muted-foreground mt-2 italic">
        {caption}
      </figcaption>
    </figure>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div className="not-prose rounded-lg border p-5 bg-card">
      <div className="font-semibold text-foreground mb-2 flex items-start gap-2">
        <HelpCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
        <span>{q}</span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed ml-7">{a}</p>
    </div>
  );
}
